export interface Destination {
  id: number
  name: string
  image: string
  description: string
  rating: number
  price: string
  duration: string
  location: string
  region: string
  // Detailed information for trip details page
  detailedDescription?: string
  highlights?: string[]
  itinerary?: string[]
  included?: string[]
  notIncluded?: string[]
  bestTime?: string
  difficulty?: string
  groupSize?: string
  accommodation?: string
  gallery?: string[]
  mapLocation?: {
    lat: number
    lng: number
  }
}

export const destinations: Destination[] = [
  {
    id: 1,
    name: 'Thailand',
    image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Experience the perfect blend of culture, beaches, and adventure',
    rating: 4.8,
    price: '$899',
    duration: '7 days',
    location: 'Southeast Asia',
    region: 'asia',
    detailedDescription: 'Immerse yourself in the vibrant culture of Thailand, from the bustling streets of Bangkok to the pristine beaches of Phuket. This 7-day adventure combines ancient temples, modern cities, and tropical paradise.',
    highlights: [
      'Explore the Grand Palace and Wat Pho in Bangkok',
      'Visit floating markets and traditional villages',
      'Relax on pristine beaches in Phuket',
      'Experience authentic Thai cuisine and cooking classes',
      'Discover ancient temples and Buddhist culture',
      'Enjoy traditional Thai massage and wellness'
    ],
    itinerary: [
      'Day 1: Arrival in Bangkok, city tour and temple visits',
      'Day 2: Floating markets and traditional village experience',
      'Day 3: Travel to Phuket, beach relaxation',
      'Day 4: Island hopping and snorkeling adventure',
      'Day 5: Cultural sites and local cuisine exploration',
      'Day 6: Free day for personal activities and shopping',
      'Day 7: Departure from Bangkok'
    ],
    included: [
      '6 nights accommodation in 4-star hotels',
      'All breakfasts and 4 dinners',
      'Airport transfers and domestic flights',
      'Professional English-speaking guide',
      'All entrance fees and activities',
      'Private transportation throughout'
    ],
    notIncluded: [
      'International flights',
      'Travel insurance',
      'Personal expenses and shopping',
      'Optional activities and spa treatments',
      'Alcoholic beverages'
    ],
    bestTime: 'November to March (cool and dry season)',
    difficulty: 'Easy to Moderate',
    groupSize: 'Maximum 16 people',
    accommodation: '4-star hotels and beach resorts',
    gallery: [
      'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1528181304800-259b08848526?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
    ],
    mapLocation: { lat: 13.7563, lng: 100.5018 }
  },
  {
    id: 2,
    name: 'Bali',
    image: 'https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Discover paradise with pristine beaches and spiritual temples',
    rating: 4.9,
    price: '$1,199',
    duration: '8 days',
    location: 'Indonesia',
    region: 'asia',
    detailedDescription: 'Experience the magic of Bali, where ancient traditions meet stunning natural beauty. From sacred temples to pristine beaches, discover why Bali is called the Island of the Gods.',
    highlights: [
      'Visit ancient temples including Tanah Lot and Uluwatu',
      'Explore the cultural heart of Ubud',
      'Relax on world-famous beaches in Seminyak',
      'Experience traditional Balinese ceremonies',
      'Discover rice terraces and volcanic landscapes',
      'Enjoy authentic Indonesian cuisine and cooking classes'
    ],
    itinerary: [
      'Day 1: Arrival in Denpasar, transfer to Ubud',
      'Day 2: Ubud cultural tour and rice terraces',
      'Day 3: Temple visits and traditional villages',
      'Day 4: Travel to Seminyak, beach relaxation',
      'Day 5: Island activities and water sports',
      'Day 6: Cultural workshops and local experiences',
      'Day 7: Free day for personal exploration',
      'Day 8: Departure from Denpasar'
    ],
    included: [
      '7 nights accommodation in boutique hotels',
      'All breakfasts and 5 dinners',
      'Airport transfers and private transportation',
      'Professional local guide',
      'All temple entrance fees',
      'Traditional cooking class'
    ],
    notIncluded: [
      'International flights',
      'Travel insurance',
      'Personal expenses and shopping',
      'Optional spa treatments',
      'Alcoholic beverages'
    ],
    bestTime: 'April to October (dry season)',
    difficulty: 'Easy',
    groupSize: 'Maximum 12 people',
    accommodation: 'Boutique hotels and beach resorts',
    gallery: [
      'https://images.unsplash.com/photo-1537953773345-d172ccf13cf1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1518548418730-8f0c8a0d2e20?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1559827260-dc66d52bef19?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
    ],
    mapLocation: { lat: -8.3405, lng: 115.0920 }
  },
  {
    id: 3,
    name: 'Maldives',
    image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Luxury overwater bungalows and crystal clear waters',
    rating: 4.9,
    price: '$2,499',
    duration: '6 days',
    location: 'Indian Ocean',
    region: 'asia',
    detailedDescription: 'Escape to paradise in the Maldives, where luxury meets nature in perfect harmony. Experience overwater bungalows, pristine beaches, and world-class diving in this tropical haven.',
    highlights: [
      'Stay in luxurious overwater bungalows',
      'World-class snorkeling and diving experiences',
      'Private beach access and water sports',
      'Sunset dolphin watching cruises',
      'Spa treatments with ocean views',
      'Fine dining with fresh seafood'
    ],
    itinerary: [
      'Day 1: Arrival in Male, seaplane transfer to resort',
      'Day 2: Resort orientation and beach relaxation',
      'Day 3: Snorkeling and diving adventures',
      'Day 4: Water sports and island exploration',
      'Day 5: Spa day and sunset cruise',
      'Day 6: Departure from Male'
    ],
    included: [
      '5 nights in overwater bungalow',
      'All meals and premium beverages',
      'Seaplane transfers',
      'Snorkeling equipment and activities',
      'Spa treatment voucher',
      'Sunset dolphin cruise'
    ],
    notIncluded: [
      'International flights',
      'Travel insurance',
      'Personal expenses and shopping',
      'Additional spa treatments',
      'Premium alcohol'
    ],
    bestTime: 'November to April (dry season)',
    difficulty: 'Easy',
    groupSize: 'Maximum 8 people',
    accommodation: '5-star overwater bungalows',
    gallery: [
      'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80'
    ],
    mapLocation: { lat: 3.2028, lng: 73.2207 }
  },
  {
    id: 4,
    name: 'Santorini',
    image: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Stunning sunsets and white-washed buildings overlooking the Aegean',
    rating: 4.7,
    price: '$1,899',
    duration: '7 days',
    location: 'Greece',
    region: 'europe'
  },
  {
    id: 5,
    name: 'Machu Picchu',
    image: 'https://images.unsplash.com/photo-1587595431973-160d0d94add1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Ancient Incan citadel high in the Andes Mountains',
    rating: 4.8,
    price: '$1,599',
    duration: '9 days',
    location: 'Peru',
    region: 'americas'
  },
  {
    id: 6,
    name: 'Safari Adventure',
    image: 'https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80',
    description: 'Wildlife encounters in the heart of the African savanna',
    rating: 4.9,
    price: '$3,299',
    duration: '10 days',
    location: 'Kenya',
    region: 'africa'
  }
]

export const regions = [
  { value: 'all', label: 'All Regions' },
  { value: 'asia', label: 'Asia' },
  { value: 'europe', label: 'Europe' },
  { value: 'americas', label: 'Americas' },
  { value: 'africa', label: 'Africa' },
  { value: 'oceania', label: 'Oceania' }
]

export const getFeaturedDestinations = (): Destination[] => {
  return destinations.slice(0, 3)
}

export const filterDestinations = (searchTerm: string, selectedRegion: string): Destination[] => {
  return destinations.filter(destination => {
    const matchesSearch = destination.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         destination.location.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRegion = selectedRegion === 'all' || destination.region === selectedRegion
    return matchesSearch && matchesRegion
  })
}
